import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  Divider,
  TextareaAutosize,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  GlassInfoCard,
  GlassInfoItem,
  GlassInfoIcon,
  GlassInfoText,
  GlassModal,
  GlassModalContent,
  GlassTextField,
  GlassButton,
  GlassButtonSecondary,
  GlassIconButton,
  GlassChip,
  SectionHeader,
  BodyText,
} from '../../../../components/glassmorphism/GlassmorphismStyles';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MaleIcon from '@mui/icons-material/Male';
import EditIcon from '@mui/icons-material/Edit';
import WorkIcon from '@mui/icons-material/Work';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import ChatIcon from '@mui/icons-material/Chat';
import Feed from '@mui/icons-material/Feed';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './styles.css';
import {
  UpdateUserDetailsCommand,
  UserCategoryViewModel,
  UserDetailsViewModel,
  CategoryViewModel,
  AddUserCategoryViewModel,
} from '../../../../configuration/api/apiSchema';
import BusinessLocationMap from './BusinessLocationMap';
import BusinessLocationAutocomplete from './BusinessLocationAutocomplete';
import CategoryTreeSelect from '../../../Categories/CategoryTreeSelect/CategoryTreeSelect';
import {
  flattenCategories,
  getAllCategoryKeys,
} from '../../../Categories/helper';
import { connect } from 'react-redux';
import { HandymanAppState } from '../../../../configuration/redux/reducers';
import {
  getUserCategories,
  getAllCategories,
  addUserCategories,
  removeUserCategories,
} from '../../../Categories/actions';
import { openStripeOnboardingLink } from '../../../Categories/actions';
import { AuthData } from '../../../Auth/reducer';
import {
  getCurrentUserDetails,
  getUserDetails,
  updateUserDetails,
  UserRoles,
} from '../../../Auth/actions';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createOrGetChat, withdrawBalance } from '../../../Orders/actions';
import { useAppDispatch } from '../../../../configuration/redux/hooks';
import { startUserKyc } from '../../../Categories/actions';

// Category interface is imported from CategoryTreeSelect component
interface Category {
  key: string;
  name: string;
  subcategories: Category[];
}

// Transform CategoryViewModel to Category interface
const transformCategoryViewModel = (
  categories: CategoryViewModel[] | null | undefined,
): Category[] => {
  if (!categories || !Array.isArray(categories)) {
    return [];
  }

  return categories
    .filter((cat) => cat && cat.key && cat.name) // Filter out invalid categories
    .map((cat) => ({
      key: cat.key || '',
      name: cat.name || '',
      subcategories: transformCategoryViewModel(cat.subcategories),
    }));
};

interface StateFromProps {
  isCategoriesLoading: boolean;
  isDetailsUpdated: boolean;
  userCategories?: UserCategoryViewModel[];
  allCategories?: CategoryViewModel[];
  isAddingCategories?: boolean;
  isRemovingCategories?: boolean;
  authData?: AuthData;
  userDetails?: UserDetailsViewModel;
  currentUser?: UserDetailsViewModel;
  isUserLoading: boolean;
}

interface DispatchFromProps {
  getUserCategories: (userId?: string) => void;
  getAllCategories: (languageKey?: string) => void;
  addUserCategories: (
    newCategories: CategoryViewModel[],
    existingCategories: UserCategoryViewModel[],
  ) => void;
  removeUserCategories: (
    categoryKeysToRemove: string[],
    existingCategories: UserCategoryViewModel[],
  ) => void;
  getUserDetails: (userId?: string) => void;
  updateUserDetails: (command: UpdateUserDetailsCommand) => void;
}

interface PassedProps {
  userId?: string;
}

const UserDetails = (
  props: PassedProps & DispatchFromProps & StateFromProps,
) => {
  const isReadonly = props.userId !== undefined;
  const isSignedIn = props.authData !== undefined;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // NEW: get dispatch & navigate
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUserId = props.currentUser?.id;

  // NEW: helper to open chat
  const handleOpenChat = async () => {
    if (!currentUserId || !props.userId) return;
    try {
      const chatId = await dispatch(
        createOrGetChat([currentUserId, props.userId]) as any,
      );
      if (chatId) {
        navigate(`/chats/${chatId}`);
      } else {
        console.error('Failed to get chat ID from response');
      }
    } catch (error) {
      console.error('Failed to open or create chat:', error);
    }
  };

  const [kycBusy, setKycBusy] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState('contact');
  const [formValues, setFormValues] = useState({
    email: '',
    location: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: '',
    bio: '',
    categories: [] as string[],
    orders: [
      'Website Development 0/10',
      'Website Fixes 0/10',
      'Website Layout 0/10',
    ],
    businessLocation: '',
    serviceAreaAddress: '',
  });
  const balance = props.userDetails?.balance ?? 0;
  const balanceInTransfer = props.userDetails?.balanceInTransfer ?? 0;
  const canWithdraw = balance > 0;
  const [isWithdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawError, setWithdrawError] = useState('');

  // Categories modal state
  const [addCategoriesModalOpen, setAddCategoriesModalOpen] = useState(false);
  const [selectedNewCategories, setSelectedNewCategories] = useState<
    Set<string>
  >(new Set());

  const handleOpenAddCategories = async () => {
    setAddCategoriesModalOpen(true);
    setSelectedNewCategories(new Set());

    // Fetch all available categories from backend
    props.getAllCategories();
  };

  const handleCloseAddCategories = () => {
    setAddCategoriesModalOpen(false);
    setSelectedNewCategories(new Set());
  };

  const toggleCategory = (categoryKey: string, isLeaf: boolean) => {
    // Only allow leaf categories (services) to be selected
    if (!isLeaf) {
      return;
    }

    setSelectedNewCategories((prev) => {
      const updated = new Set(prev);
      if (updated.has(categoryKey)) {
        updated.delete(categoryKey);
      } else {
        updated.add(categoryKey);
      }
      return updated;
    });
  };

  const handleRemoveCategory = async (categoryToRemove: string) => {
    // Find the category key from user categories
    const categoryToRemoveObj = props.userCategories?.find(
      (cat) => cat.name === categoryToRemove,
    );
    if (categoryToRemoveObj?.key) {
      await props.removeUserCategories(
        [categoryToRemoveObj.key],
        props.userCategories || [],
      );
    }
  };

  const handleSaveNewCategories = async () => {
    if (selectedNewCategories.size > 0) {
      // Find the complete category objects for the selected keys
      const findCategoriesByKeys = (
        categories: CategoryViewModel[],
        keys: string[],
      ): CategoryViewModel[] => {
        const result: CategoryViewModel[] = [];

        const searchCategories = (cats: CategoryViewModel[]) => {
          for (const cat of cats) {
            if (cat.key && keys.includes(cat.key)) {
              result.push(cat);
            }
            if (cat.subcategories && cat.subcategories.length > 0) {
              searchCategories(cat.subcategories);
            }
          }
        };

        searchCategories(categories);
        return result;
      };

      const selectedCategoryKeys = Array.from(selectedNewCategories);
      const selectedCategoryObjects = findCategoriesByKeys(
        props.allCategories || [],
        selectedCategoryKeys,
      );

      await props.addUserCategories(
        selectedCategoryObjects,
        props.userCategories || [],
      );
      handleCloseAddCategories();
    }
  };

  // Get available categories (exclude user's current categories) and transform to tree structure
  const getAvailableCategories = (): Category[] => {
    if (!props.allCategories || !Array.isArray(props.allCategories)) {
      return [];
    }

    const userCategoryKeys = new Set(
      props.userCategories?.map((cat) => cat.key).filter(Boolean) || [],
    );

    // Recursively filter out user's current categories from tree structure
    const filterCategories = (
      categories: CategoryViewModel[],
    ): CategoryViewModel[] => {
      if (!categories || !Array.isArray(categories)) {
        return [];
      }

      return categories
        .map((cat) => {
          if (!cat || !cat.key) return null;

          // If this category is already selected by the user, exclude it
          if (userCategoryKeys.has(cat.key)) {
            return null;
          }

          // Recursively filter subcategories
          const filteredSubcategories = filterCategories(
            cat.subcategories || [],
          );

          return {
            ...cat,
            subcategories:
              filteredSubcategories.length > 0 ? filteredSubcategories : null,
          };
        })
        .filter((cat): cat is NonNullable<typeof cat> => cat !== null);
    };

    try {
      const filteredCategories = filterCategories(props.allCategories);
      return transformCategoryViewModel(filteredCategories);
    } catch (error) {
      console.error('Error filtering categories:', error);
      return [];
    }
  };

  useEffect(() => {
    if (props.authData && !props.userCategories) {
      if (props.userId) {
        props.getUserCategories(props.userId);
      } else {
        props.getUserCategories();
      }
    }
  }, [props.authData?.access_token]); // Only trigger when access token changes, not entire authData object

  useEffect(() => {
    if (props.userCategories) {
      setFormValues((prev) => ({
        ...prev,
        categories: props.userCategories?.map((x) => x.name as string) || [],
      }));
    }
  }, [props.userCategories, props.isCategoriesLoading]);

  useEffect(() => {
    if (props.userDetails) {
      setFormValues((prev) => {
        const hasUserSelectedBusinessLocation =
          prev.businessLocation &&
          prev.businessLocation !== props.userDetails?.serviceAreaAddress;

        return {
          ...prev,
          firstName: props.userDetails?.firstName ?? prev.firstName,
          lastName: props.userDetails?.lastName ?? prev.lastName,
          email: props.userDetails?.email ?? prev.email,
          birthDate: props.userDetails?.birthDate
            ? props.userDetails.birthDate.split('T')[0]
            : prev.birthDate,
          gender: props.userDetails?.gender ?? prev.gender,
          location: props.userDetails?.serviceAreaAddress ?? prev.location,
          bio: props.userDetails?.bio ?? prev.bio,
          categories: prev.categories,
          serviceAreaAddress:
            props.userDetails?.serviceAreaAddress ?? prev.serviceAreaAddress,
          businessLocation: hasUserSelectedBusinessLocation
            ? prev.businessLocation
            : (props.userDetails?.serviceAreaAddress ?? prev.businessLocation),
        };
      });
    }
  }, [props.userDetails, props.isCategoriesLoading]);

  useEffect(() => {
    if (props.isDetailsUpdated) {
      if (props.userId) {
        props.getUserDetails(props.userId);
      } else {
        props.getUserDetails();
      }

      handleClose();
    }
  }, [props.isDetailsUpdated]);

  const handleKycClick = async () => {
    try {
      setKycBusy(true);
      const res = await dispatch(startUserKyc() as any);

      if (!res) return; // nothing to do on 404

      window.open(res.shareableUrl!, '_blank', 'noopener,noreferrer');
    } finally {
      setKycBusy(false);
    }
  };

  const handleStripeClick = async () => {
    try {
      setStripeBusy(true);
      await dispatch(openStripeOnboardingLink() as any);
    } finally {
      setStripeBusy(false);
    }
  };

  const handleOpen = (type: string) => {
    setModalType(type);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>,
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name!]: value }));
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    props.updateUserDetails({
      // email is disabled - users should contact support to change it
      // email: formValues.email,
      birthDate: formValues.birthDate,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      bio: formValues.bio,
      gender: formValues.gender,
      location: formValues.location,
      serviceArea: formValues.businessLocation
        ? formValues.businessLocation
        : formValues.serviceAreaAddress,
    } as UpdateUserDetailsCommand);
  };

  const handleConfirmWithdraw = () => {
    const numericAmount = parseFloat(withdrawAmount);
    const max = props.userDetails?.balance ?? 0;

    if (isNaN(numericAmount) || numericAmount <= 0) {
      setWithdrawError('Amount must be greater than 0.');
      return;
    }

    if (numericAmount > max) {
      setWithdrawError(
        `Amount cannot exceed your balance (${max.toFixed(2)} GBP).`,
      );
      return;
    }

    setWithdrawError('');
    dispatch(withdrawBalance(numericAmount));
    setWithdrawModalOpen(false);
    setWithdrawAmount('');
  };

  return (
    <>
      {/* Contact Information Section */}
      <Box
        sx={{
          fontFamily: 'Montserrat',
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '24px',
          lineHeight: '133.4%',
          textAlign: 'left',
          color: isDark ? '#00FFFF' : '#005CAD',
          mb: 3,
        }}
      >
        Contact Information
      </Box>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: '24px',
              gap: '16px',
              background: isDark ? '#1A1A1A' : '#E5E5DC',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow:
                '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
              borderRadius: '32px',
              position: 'relative',
              minHeight: '352px',
            }}
          >
            {/* First Name */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '16px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                  gap: '8px',
                  width: '48px',
                  height: '48px',
                  background: isDark
                    ? 'rgba(0, 92, 173, 0.7)'
                    : 'rgba(0, 36, 58, 0.8)',
                  backgroundBlendMode: 'darken',
                  boxShadow:
                    '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                  backdropFilter: 'blur(6px)',
                  borderRadius: '64px',
                }}
              >
                <DriveFileRenameOutlineIcon
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: '#E5E5DC',
                    filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: '14px',
                    lineHeight: '157%',
                    letterSpacing: '0.1px',
                    color: isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  First Name
                </Box>
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                    letterSpacing: '0.15px',
                    color: isDark ? '#D5D5D5' : '#585858',
                  }}
                >
                  {formValues.firstName || 'Not provided'}
                </Box>
              </Box>
            </Box>

            {/* Last Name */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '16px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                  gap: '8px',
                  width: '48px',
                  height: '48px',
                  background: isDark
                    ? 'rgba(0, 92, 173, 0.7)'
                    : 'rgba(0, 36, 58, 0.8)',
                  backgroundBlendMode: 'darken',
                  boxShadow:
                    '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                  backdropFilter: 'blur(6px)',
                  borderRadius: '64px',
                }}
              >
                <DriveFileRenameOutlineIcon
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: '#E5E5DC',
                    filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: '14px',
                    lineHeight: '157%',
                    letterSpacing: '0.1px',
                    color: isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Last Name
                </Box>
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                    letterSpacing: '0.15px',
                    color: isDark ? '#D5D5D5' : '#585858',
                  }}
                >
                  {formValues.lastName || 'Not provided'}
                </Box>
              </Box>
            </Box>

            {/* Business Location */}
            {props.userDetails?.roleId === UserRoles.HandymanRoleId && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '16px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '8px',
                    gap: '8px',
                    width: '48px',
                    height: '48px',
                    background: isDark
                      ? 'rgba(0, 92, 173, 0.7)'
                      : 'rgba(0, 36, 58, 0.8)',
                    backgroundBlendMode: 'darken',
                    boxShadow:
                      '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                    backdropFilter: 'blur(6px)',
                    borderRadius: '64px',
                  }}
                >
                  <LocationOnIcon
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: '#E5E5DC',
                      filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '2px',
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '157%',
                      letterSpacing: '0.1px',
                      color: isDark ? '#B6B6B6' : '#1A1A1A',
                    }}
                  >
                    Business Location
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '150%',
                      letterSpacing: '0.15px',
                      color: isDark ? '#D5D5D5' : '#585858',
                    }}
                  >
                    {formValues.serviceAreaAddress || 'Not provided'}
                  </Box>
                </Box>
              </Box>
            )}

            {/* Balance */}
            {!isReadonly &&
              props.userDetails?.roleId === UserRoles.HandymanRoleId && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '16px',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '8px',
                      gap: '8px',
                      width: '48px',
                      height: '48px',
                      background: isDark
                        ? 'rgba(0, 92, 173, 0.7)'
                        : 'rgba(0, 36, 58, 0.8)',
                      backgroundBlendMode: 'darken',
                      boxShadow:
                        '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                      backdropFilter: 'blur(6px)',
                      borderRadius: '64px',
                    }}
                  >
                    <AssignmentIcon
                      sx={{
                        width: '24px',
                        height: '24px',
                        color: '#E5E5DC',
                        filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      padding: '0px',
                      gap: '2px',
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: 'Roboto',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '157%',
                        letterSpacing: '0.1px',
                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                      }}
                    >
                      Balance
                    </Box>
                    <Box
                      sx={{
                        fontFamily: 'Roboto',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '150%',
                        letterSpacing: '0.15px',
                        color: '#29A322',
                      }}
                    >
                      {balance.toFixed(2)} GBP
                    </Box>
                  </Box>
                </Box>
              )}

            {/* In Transfer */}
            {!isReadonly &&
              props.userDetails?.roleId === UserRoles.HandymanRoleId && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '16px',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '8px',
                      gap: '8px',
                      width: '48px',
                      height: '48px',
                      background: isDark
                        ? 'rgba(0, 92, 173, 0.7)'
                        : 'rgba(0, 36, 58, 0.8)',
                      backgroundBlendMode: 'darken',
                      boxShadow:
                        '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                      backdropFilter: 'blur(6px)',
                      borderRadius: '64px',
                    }}
                  >
                    <AssignmentIcon
                      sx={{
                        width: '24px',
                        height: '24px',
                        color: '#E5E5DC',
                        filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      padding: '0px',
                      gap: '2px',
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: 'Roboto',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '157%',
                        letterSpacing: '0.1px',
                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                      }}
                    >
                      In Transfer
                    </Box>
                    <Box
                      sx={{
                        fontFamily: 'Roboto',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '150%',
                        letterSpacing: '0.15px',
                        color: '#D1AB01',
                      }}
                    >
                      {balanceInTransfer.toFixed(2)} GBP
                    </Box>
                  </Box>
                </Box>
              )}
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: '24px',
              gap: '16px',
              background: isDark ? '#1A1A1A' : '#E5E5DC',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow:
                '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
              borderRadius: '32px',
              position: 'relative',
              minHeight: '352px',
            }}
          >
            {/* Email with Edit Button */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0px',
                gap: '16px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '16px',
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '8px',
                    gap: '8px',
                    width: '48px',
                    height: '48px',
                    background: isDark
                      ? 'rgba(0, 92, 173, 0.7)'
                      : 'rgba(0, 36, 58, 0.8)',
                    backgroundBlendMode: 'darken',
                    boxShadow:
                      '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                    backdropFilter: 'blur(6px)',
                    borderRadius: '64px',
                  }}
                >
                  <EmailIcon
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: '#E5E5DC',
                      filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '2px',
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '157%',
                      letterSpacing: '0.1px',
                      color: isDark ? '#B6B6B6' : '#1A1A1A',
                    }}
                  >
                    Email
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '150%',
                      letterSpacing: '0.15px',
                      color: isDark ? '#D5D5D5' : '#585858',
                    }}
                  >
                    {isSignedIn ? (
                      formValues.email
                    ) : (
                      <>
                        <Link
                          to={'/sign-in'}
                          style={{
                            color: isDark ? '#00FFFF' : '#005CAD',
                            textDecoration: 'none',
                          }}
                        >
                          Sign in
                        </Link>{' '}
                        to view contacts
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Edit Button */}
              {!isReadonly && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '8px',
                    gap: '8px',
                    width: '48px',
                    height: '48px',
                    background: 'rgba(153, 153, 153, 0.3)',
                    backgroundBlendMode: 'darken',
                    boxShadow:
                      '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                    backdropFilter: 'blur(6px)',
                    borderRadius: '64px',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleOpen('contact')}
                >
                  <EditIcon
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: isDark ? '#B6B6B6' : '#1A1A1A',
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Birth Date */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '16px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                  gap: '8px',
                  width: '48px',
                  height: '48px',
                  background: isDark
                    ? 'rgba(0, 92, 173, 0.7)'
                    : 'rgba(0, 36, 58, 0.8)',
                  backgroundBlendMode: 'darken',
                  boxShadow:
                    '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                  backdropFilter: 'blur(6px)',
                  borderRadius: '64px',
                }}
              >
                <CalendarTodayIcon
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: '#E5E5DC',
                    filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: '14px',
                    lineHeight: '157%',
                    letterSpacing: '0.1px',
                    color: isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Birth Date
                </Box>
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                    letterSpacing: '0.15px',
                    color: isDark ? '#D5D5D5' : '#585858',
                  }}
                >
                  {formValues.birthDate
                    ? new Date(formValues.birthDate).toLocaleDateString()
                    : 'Not provided'}
                </Box>
              </Box>
            </Box>

            {/* Gender */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '16px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                  gap: '8px',
                  width: '48px',
                  height: '48px',
                  background: isDark
                    ? 'rgba(0, 92, 173, 0.7)'
                    : 'rgba(0, 36, 58, 0.8)',
                  backgroundBlendMode: 'darken',
                  boxShadow:
                    '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                  backdropFilter: 'blur(6px)',
                  borderRadius: '64px',
                }}
              >
                <MaleIcon
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: '#E5E5DC',
                    filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: '14px',
                    lineHeight: '157%',
                    letterSpacing: '0.1px',
                    color: isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Gender
                </Box>
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                    letterSpacing: '0.15px',
                    color: isDark ? '#D5D5D5' : '#585858',
                  }}
                >
                  {formValues.gender || 'Not specified'}
                </Box>
              </Box>
            </Box>

            {/* KYC Verification with Button */}
            {!isReadonly && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0px',
                  gap: '16px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '16px',
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '8px',
                      gap: '8px',
                      width: '48px',
                      height: '48px',
                      background: isDark
                        ? 'rgba(0, 92, 173, 0.7)'
                        : 'rgba(0, 36, 58, 0.8)',
                      backgroundBlendMode: 'darken',
                      boxShadow:
                        '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                      backdropFilter: 'blur(6px)',
                      borderRadius: '64px',
                    }}
                  >
                    <Feed
                      sx={{
                        width: '24px',
                        height: '24px',
                        color: '#E5E5DC',
                        filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      padding: '0px',
                      gap: '2px',
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: 'Roboto',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '157%',
                        letterSpacing: '0.1px',
                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                      }}
                    >
                      KYC Verification
                    </Box>
                    <Box
                      sx={{
                        fontFamily: 'Roboto',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '150%',
                        letterSpacing: '0.15px',
                        color:
                          props.userDetails?.kycStatus === 'Verified'
                            ? '#4CAF50'
                            : props.userDetails?.kycStatus === 'Pending'
                              ? '#FF9800'
                              : props.userDetails?.kycStatus === 'Review'
                                ? '#FF9800'
                                : '#F44336',
                      }}
                    >
                      {(() => {
                        switch (props.userDetails?.kycStatus) {
                          case 'Pending':
                            return 'Pending';
                          case 'Verified':
                            return 'Verified';
                          case 'Failed':
                            return 'Failed';
                          case 'Review':
                            return 'Marked for manual review';
                          default:
                            return 'Not verified';
                        }
                      })()}
                    </Box>
                  </Box>
                </Box>

                {/* Check Status Button */}
                {['NotStarted', 'Pending', 'Failed'].includes(
                  props.userDetails?.kycStatus ?? 'NotStarted',
                ) && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '4px 16px',
                      gap: '8px',
                      background: isDark
                        ? 'rgba(0, 92, 173, 0.7)'
                        : 'rgba(0, 36, 58, 0.8)',
                      boxShadow:
                        '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                      backdropFilter: 'blur(6px)',
                      borderRadius: '64px',
                      cursor: 'pointer',
                      minWidth: '151px',
                      height: '40px',
                    }}
                    onClick={handleKycClick}
                  >
                    {kycBusy ? (
                      <CircularProgress size={20} sx={{ color: '#E2F7FF' }} />
                    ) : (
                      <>
                        <LaunchIcon
                          sx={{
                            width: '20px',
                            height: '20px',
                            color: '#E2F7FF',
                            filter:
                              'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                          }}
                        />
                        <Box
                          sx={{
                            fontFamily: 'Montserrat',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '29px',
                            color: '#FFFFFF',
                          }}
                        >
                          {props.userDetails?.kycStatus === 'Pending'
                            ? 'Continue'
                            : props.userDetails?.kycStatus === 'Failed'
                              ? 'Retry'
                              : 'Verify'}
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Stripe Payment Verification with Button */}
            {!isReadonly && props.authData?.role === 'handyman' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: '0px',
                  gap: '16px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '8px',
                    width: '48px',
                    height: '48px',
                    background: isDark
                      ? 'rgba(0, 92, 173, 0.7)'
                      : 'rgba(0, 36, 58, 0.8)',
                    backgroundBlendMode: 'darken',
                    boxShadow:
                      '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                    backdropFilter: 'blur(6px)',
                    borderRadius: '64px',
                  }}
                >
                  <AttachMoneyIcon
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: '#E5E5DC',
                      filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '2px',
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '157%',
                      letterSpacing: '0.1px',
                      color: isDark ? '#B6B6B6' : '#1A1A1A',
                    }}
                  >
                    Stripe Connect
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '150%',
                      letterSpacing: '0.15px',
                      color: props.userDetails?.isStripeAccountVerified
                        ? '#4CAF50'
                        : '#F44336',
                    }}
                  >
                    {props.userDetails?.isStripeAccountVerified
                      ? 'Connected'
                      : 'Not Connected'}
                  </Box>
                </Box>

                {/* Connect Stripe Button */}
                {!props.userDetails?.isStripeAccountVerified && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '4px 16px',
                      gap: '8px',
                      background: isDark
                        ? 'rgba(0, 92, 173, 0.7)'
                        : 'rgba(0, 36, 58, 0.8)',
                      boxShadow:
                        '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                      backdropFilter: 'blur(6px)',
                      borderRadius: '64px',
                      cursor: 'pointer',
                      minWidth: '151px',
                      height: '40px',
                    }}
                    onClick={handleStripeClick}
                  >
                    {stripeBusy ? (
                      <CircularProgress size={20} sx={{ color: '#E2F7FF' }} />
                    ) : (
                      <>
                        <LaunchIcon
                          sx={{
                            width: '20px',
                            height: '20px',
                            color: '#E2F7FF',
                            filter:
                              'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                          }}
                        />
                        <Box
                          sx={{
                            fontFamily: 'Montserrat',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '29px',
                            color: '#FFFFFF',
                          }}
                        >
                          Connect Stripe
                        </Box>
                      </>
                    )}
                  </Box>
                )}

                {/* Verified Badge */}
                {props.userDetails?.isStripeAccountVerified && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '4px 16px',
                      gap: '8px',
                      background:
                        'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                      borderRadius: '64px',
                      minWidth: '151px',
                      height: '40px',
                    }}
                  >
                    <CheckCircleIcon
                      sx={{ width: '20px', height: '20px', color: '#FFFFFF' }}
                    />
                    <Box
                      sx={{
                        fontFamily: 'Montserrat',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '29px',
                        color: '#FFFFFF',
                      }}
                    >
                      Verified
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Message Button for other users */}
            {isSignedIn &&
              currentUserId &&
              props.userId &&
              currentUserId !== props.userId && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '4px 16px',
                    gap: '8px',
                    background: isDark
                      ? 'rgba(0, 92, 173, 0.7)'
                      : 'rgba(0, 36, 58, 0.8)',
                    boxShadow:
                      '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                    backdropFilter: 'blur(6px)',
                    borderRadius: '64px',
                    cursor: 'pointer',
                    width: '151px',
                    height: '40px',
                    mt: 2,
                  }}
                  onClick={handleOpenChat}
                >
                  <ChatIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      color: '#E2F7FF',
                      filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                    }}
                  />
                  <Box
                    sx={{
                      fontFamily: 'Montserrat',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '29px',
                      color: '#FFFFFF',
                    }}
                  >
                    Message
                  </Box>
                </Box>
              )}
          </Box>
        </Grid>
      </Grid>

      {/* About Me Section */}
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '24px',
          gap: '16px',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow:
            '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
          borderRadius: '32px',
          position: 'relative',
          width: '100%',
          mt: 3,
          minHeight: '136px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0px',
            gap: '16px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              fontFamily: 'Montserrat',
              fontStyle: 'normal',
              fontWeight: 800,
              fontSize: '20px',
              lineHeight: '160%',
              letterSpacing: '0.15px',
              color: isDark ? '#B6B6B6' : '#1A1A1A',
            }}
          >
            About Me
          </Box>

          {!isReadonly && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px',
                gap: '8px',
                width: '48px',
                height: '48px',
                background: 'rgba(153, 153, 153, 0.3)',
                backgroundBlendMode: 'darken',
                boxShadow:
                  '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                backdropFilter: 'blur(6px)',
                borderRadius: '64px',
                cursor: 'pointer',
              }}
              onClick={() => handleOpen('bio')}
            >
              <EditIcon
                sx={{
                  width: '24px',
                  height: '24px',
                  color: isDark ? '#B6B6B6' : '#1A1A1A',
                }}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '150%',
            letterSpacing: '0.15px',
            color: isDark ? '#D5D5D5' : '#585858',
          }}
        >
          {formValues.bio || ''}
        </Box>
      </Box>

      {/* Order Categories Section */}
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '24px',
          gap: '16px',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow:
            '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
          borderRadius: '32px',
          position: 'relative',
          width: '100%',
          mt: 3,
          minHeight: '192px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0px',
            gap: '16px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              fontFamily: 'Montserrat',
              fontStyle: 'normal',
              fontWeight: 800,
              fontSize: '20px',
              lineHeight: '160%',
              letterSpacing: '0.15px',
              color: isDark ? '#B6B6B6' : '#1A1A1A',
            }}
          >
            Order Categories
          </Box>

          {!isReadonly && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px',
                gap: '8px',
                width: '48px',
                height: '48px',
                background: 'rgba(153, 153, 153, 0.3)',
                backgroundBlendMode: 'darken',
                boxShadow:
                  '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                backdropFilter: 'blur(6px)',
                borderRadius: '64px',
                cursor: 'pointer',
              }}
              onClick={handleOpenAddCategories}
            >
              <EditIcon
                sx={{
                  width: '24px',
                  height: '24px',
                  color: isDark ? '#B6B6B6' : '#1A1A1A',
                }}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '150%',
            letterSpacing: '0.15px',
            color: isDark ? '#D5D5D5' : '#585858',
          }}
        ></Box>

        {/* Categories Chips */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            padding: '0px',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          {formValues.categories.map((category, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 24px',
                gap: '8px',
                background: isDark ? '#1A1A1A' : '#E5E5DC',
                boxShadow:
                  '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                backdropFilter: 'blur(6px)',
                borderRadius: '64px',
                height: '40px',
              }}
            >
              <WorkIcon
                sx={{
                  width: '20px',
                  height: '20px',
                  color: isDark ? '#D5D5D5' : '#585858',
                }}
              />
              <Box
                sx={{
                  fontFamily: 'Montserrat',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '29px',
                  color: isDark ? '#D5D5D5' : '#585858',
                }}
              >
                {category}
              </Box>
              {!isReadonly && (
                <Box
                  onClick={() => handleRemoveCategory(category)}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover svg': {
                      color: '#F44336',
                    },
                  }}
                >
                  {props.isRemovingCategories ? (
                    <CircularProgress
                      size={16}
                      sx={{ color: isDark ? '#D5D5D5' : '#585858' }}
                    />
                  ) : (
                    <CloseIcon
                      sx={{
                        width: '16px',
                        height: '16px',
                        color: isDark ? '#D5D5D5' : '#585858',
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          ))}

          {/* Add Category Button */}
          {!isReadonly && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '4px 16px',
                gap: '8px',
                background: 'rgba(0, 36, 58, 0.8)',
                boxShadow:
                  '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                backdropFilter: 'blur(6px)',
                borderRadius: '64px',
                cursor: 'pointer',
                height: '40px',
              }}
              onClick={handleOpenAddCategories}
            >
              <AddIcon
                sx={{
                  width: '20px',
                  height: '20px',
                  color: '#E2F7FF',
                  filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                }}
              />
              <Box
                sx={{
                  fontFamily: 'Montserrat',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '29px',
                  color: '#FFFFFF',
                }}
              >
                Add category
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Add Categories Modal */}
      <GlassModal
        open={addCategoriesModalOpen}
        onClose={handleCloseAddCategories}
        aria-labelledby='add-categories-modal'
      >
        <GlassModalContent
          sx={{
            width: { xs: '95vw', sm: '90vw', md: 500 },
            maxWidth: { xs: '95vw', sm: '90vw', md: 500 },
            maxHeight: { xs: '90vh', md: '80vh' },
          }}
        >
          {/* Close Icon */}
          <GlassIconButton
            size='small'
            onClick={handleCloseAddCategories}
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 16 },
              right: { xs: 8, sm: 16 },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </GlassIconButton>

          <SectionHeader
            sx={{
              mb: 2,
              pr: { xs: 4, sm: 5 },
              fontSize: { xs: '18px', sm: '20px' },
            }}
          >
            Add Categories
          </SectionHeader>
          <Divider
            sx={{
              mb: 3,
              background:
                'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
              border: 'none',
              height: '1px',
            }}
          />

          {props.isCategoriesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress sx={{ color: '#00E8E7' }} />
            </Box>
          ) : (
            <>
              <BodyText sx={{ mb: 3, fontSize: '14px' }}>
                Select categories you want to add to your profile:
              </BodyText>

              <Box
                sx={{
                  mb: 3,
                  maxHeight: { xs: '50vh', sm: '300px' },
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 92, 173, 0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'rgba(0, 92, 173, 0.5)',
                    },
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor:
                    'rgba(0, 92, 173, 0.3) rgba(255, 255, 255, 0.1)',
                }}
              >
                {getAvailableCategories().length > 0 ? (
                  <CategoryTreeSelect
                    selectedCategoryKeys={selectedNewCategories}
                    toggleCategory={toggleCategory}
                    isSingleSelect={false}
                    categories={getAvailableCategories()}
                    isDisabledCheckboxes={false}
                    onlyLeafSelectable={true}
                  />
                ) : (
                  <BodyText
                    sx={{ textAlign: 'center', py: 3, fontSize: '14px' }}
                  >
                    No additional categories available to add.
                  </BodyText>
                )}
              </Box>
            </>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3,
            }}
          >
            <GlassButtonSecondary
              onClick={handleCloseAddCategories}
              disabled={props.isCategoriesLoading || props.isAddingCategories}
              fullWidth={true}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                order: { xs: 2, sm: 1 },
              }}
            >
              Cancel
            </GlassButtonSecondary>
            <GlassButton
              onClick={handleSaveNewCategories}
              disabled={
                selectedNewCategories.size === 0 ||
                props.isCategoriesLoading ||
                props.isAddingCategories
              }
              fullWidth={true}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                order: { xs: 1, sm: 2 },
              }}
            >
              {props.isAddingCategories ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                `Save (${selectedNewCategories.size})`
              )}
            </GlassButton>
          </Box>
        </GlassModalContent>
      </GlassModal>

      <GlassModal
        open={open}
        onClose={handleClose}
        aria-labelledby='edit-user-details'
      >
        <GlassModalContent sx={{ width: 450 }}>
          {/* Close Icon */}
          <GlassIconButton
            size='small'
            onClick={handleClose}
            sx={{ position: 'absolute', top: 16, right: 16 }}
          >
            <CloseIcon />
          </GlassIconButton>

          <SectionHeader sx={{ mb: 2, pr: 5 }}>
            Edit{' '}
            {modalType === 'contact'
              ? 'Contact Info'
              : modalType === 'bio'
                ? 'Bio'
                : 'Categories'}
          </SectionHeader>
          <Divider
            sx={{
              mb: 3,
              background:
                'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
              border: 'none',
              height: '1px',
            }}
          />
          {modalType === 'contact' && (
            <>
              <Tooltip
                title='Please contact support to change your email address'
                placement='top'
              >
                <Box sx={{ mb: 2 }}>
                  <GlassTextField
                    fullWidth
                    label='Email'
                    name='email'
                    value={formValues.email}
                    onChange={handleInputChange}
                    disabled
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'rgba(0, 0, 0, 0.6)',
                      },
                    }}
                  />
                </Box>
              </Tooltip>
              <GlassTextField
                fullWidth
                label='First Name'
                name='firstName'
                value={formValues.firstName}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <GlassTextField
                fullWidth
                label='Last Name'
                name='lastName'
                value={formValues.lastName}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <GlassTextField
                fullWidth
                label='Birth Date'
                name='birthDate'
                type='date'
                value={formValues.birthDate}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <GlassTextField
                select
                fullWidth
                label='Gender'
                name='gender'
                value={formValues.gender}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              >
                {['Male', 'Female', 'Other'].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </GlassTextField>
              <Box sx={{ mb: 3 }}>
                <BodyText sx={{ mb: 1, fontSize: '14px', fontWeight: 500 }}>
                  Location
                </BodyText>
                <BusinessLocationAutocomplete
                  value={
                    formValues.businessLocation
                      ? formValues.businessLocation
                      : formValues.serviceAreaAddress
                  }
                  onChange={(val) => {
                    setFormValues((prev) => {
                      const updated = { ...prev, businessLocation: val };
                      return updated;
                    });
                  }}
                  setFormValues={setFormValues}
                />
              </Box>
            </>
          )}
          {modalType === 'bio' && (
            <Box sx={{ mb: 3 }}>
              <TextareaAutosize
                minRows={6}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '16px',
                  fontFamily: 'Roboto, sans-serif',
                  backgroundColor: isDark
                    ? 'rgba(0, 0, 0, 0.3)'
                    : 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(6px)',
                  borderRadius: '16px',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)'}`,
                  resize: 'vertical',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  color: isDark ? '#D5D5D5' : '#1A1A1A',
                }}
                name='bio'
                value={formValues.bio}
                onChange={handleTextareaChange}
                placeholder='Tell us about yourself...'
              />
            </Box>
          )}
          {modalType === 'categories' && (
            <BodyText sx={{ textAlign: 'center', py: 3, fontSize: '14px' }}>
              This modal is deprecated. Please use the new "Add Category" button
              to manage categories.
            </BodyText>
          )}
          <GlassButton
            fullWidth
            onClick={handleSubmit}
            disabled={props.isUserLoading}
            sx={{ position: 'relative' }}
          >
            {props.isUserLoading ? (
              <CircularProgress
                size={24}
                color='inherit'
                sx={{ position: 'absolute' }}
              />
            ) : (
              'Save Changes'
            )}
          </GlassButton>
        </GlassModalContent>
      </GlassModal>
      <GlassModal
        open={isWithdrawModalOpen}
        onClose={() => setWithdrawModalOpen(false)}
      >
        <GlassModalContent sx={{ width: 400 }}>
          <SectionHeader sx={{ mb: 3 }}>Withdraw Balance</SectionHeader>

          <BodyText sx={{ mb: 2, fontSize: '14px' }}>
            Available Balance:{' '}
            <strong style={{ color: '#4CAF50' }}>
              {balance.toFixed(2)} GBP
            </strong>
          </BodyText>

          <GlassTextField
            fullWidth
            label='Amount to Withdraw'
            value={withdrawAmount}
            inputProps={{
              inputMode: 'decimal',
              pattern: '^[0-9]*[.,]?[0-9]{0,2}',
            }}
            onChange={(e) => {
              const input = e.target.value;

              // Only allow numbers and decimal point
              if (!/^\d*\.?\d*$/.test(input)) return;

              const value = parseFloat(input);
              const max = props.userDetails?.balance ?? 0;

              setWithdrawAmount(input);

              if (!input || isNaN(value)) {
                setWithdrawError('Amount is required');
              } else if (value <= 0) {
                setWithdrawError('Amount must be greater than 0');
              } else if (value > max) {
                setWithdrawError(
                  `Maximum withdrawable amount is ${max.toFixed(2)} GBP`,
                );
              } else {
                setWithdrawError('');
              }
            }}
            error={!!withdrawError}
            helperText={withdrawError}
            sx={{
              mt: 1,
              mb: 4,
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
          />

          <Box display='flex' justifyContent='flex-end' gap={2}>
            <GlassButtonSecondary onClick={() => setWithdrawModalOpen(false)}>
              Cancel
            </GlassButtonSecondary>
            <GlassButton
              onClick={handleConfirmWithdraw}
              disabled={!withdrawAmount || !!withdrawError}
            >
              Confirm Withdrawal
            </GlassButton>
          </Box>
        </GlassModalContent>
      </GlassModal>
    </>
  );
};

const mapStateToProps = (state: HandymanAppState): StateFromProps => {
  return {
    isCategoriesLoading: state.category.isLoading,
    userCategories: state.category.userCategories,
    allCategories: state.category.allCategories,
    isAddingCategories: state.category.isAddingCategories,
    isRemovingCategories: state.category.isRemovingCategories,
    authData: state.user.authData,
    isDetailsUpdated: state.user.isDetailsUpdated,
    userDetails: state.user.userDetails,
    currentUser: state.user.currentUserDetails,
    isUserLoading: state.user.isLoading,
  };
};

export default connect<
  StateFromProps,
  DispatchFromProps,
  any,
  HandymanAppState
>(mapStateToProps, {
  getUserCategories,
  getAllCategories,
  addUserCategories,
  removeUserCategories,
  updateUserDetails,
  getUserDetails,
})(UserDetails);
